import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtKrShort, ORG_META, type Pipeline } from "../lib/markadur";

type Tab = "pipeline" | "customers" | "search";
type MarkadurSearch = { tab?: Tab };

export const Route = createFileRoute("/verkfaeri/markadurinn")({
  validateSearch: (search: Record<string, unknown>): MarkadurSearch => {
    const t = search.tab;
    if (t === "customers" || t === "search" || t === "pipeline")
      return { tab: t };
    return {};
  },
  component: MarkadurLayout,
});

function tabFromPath(pathname: string, search: string): Tab | null {
  if (pathname.includes("/fyrirtaeki/")) return null;
  const params = new URLSearchParams(search);
  const t = params.get("tab");
  if (t === "customers" || t === "search" || t === "pipeline") return t;
  return "pipeline";
}

function MarkadurLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.searchStr });
  const activeTab = tabFromPath(pathname, search);
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);

  useEffect(() => {
    api
      .getPipeline()
      .then(setPipeline)
      .catch(() => {});
  }, []);

  const teams = pipeline?.teams ?? [];

  return (
    <div className="flex min-h-[calc(100vh-64px)]">
      <aside className="flex w-[248px] flex-none flex-col gap-1 border-r border-line bg-white px-4 py-[22px]">
        <div className="flex items-center gap-[11px] px-2.5 pb-4 pt-1.5">
          <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[11px] bg-[#FEF1E3] font-display text-[18px] font-extrabold text-[#F0871E]">
            ⇄
          </div>
          <div>
            <div className="font-display text-[14.5px] font-bold text-ink">
              Atomika CRM
            </div>
            <div className="text-[11.5px] text-muted-2">
              Sölupípa &amp; samningar
            </div>
          </div>
        </div>

        <NavItem
          to="/verkfaeri/markadurinn"
          search={{ tab: "pipeline" }}
          active={activeTab === "pipeline"}
          label="Sölupípa"
        />
        <NavItem
          to="/verkfaeri/markadurinn"
          search={{ tab: "customers" }}
          active={activeTab === "customers"}
          label="Viðskiptavinir"
        />
        <NavItem
          to="/verkfaeri/markadurinn"
          search={{ tab: "search" }}
          active={activeTab === "search"}
          label="Fyrirtækjaleit"
        />

        <div className="flex-1" />

        <div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-4">
          Vigtuð sölupípa
        </div>
        {teams.map((t) => (
          <div
            key={t.org}
            className="mb-1.5 rounded-[14px] border border-line bg-canvas p-3"
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: ORG_META[t.org].color }}
              />
              <div className="text-[13px] font-bold text-ink">
                {ORG_META[t.org].label}
              </div>
            </div>
            <div
              className="mt-1 font-display text-[19px] font-extrabold"
              style={{ color: ORG_META[t.org].color }}
            >
              {fmtKrShort(t.openWeightedKr)}
            </div>
            <div className="text-[11px] text-muted-2">
              {t.openCount} opin · {t.wonCount} unnin
            </div>
          </div>
        ))}
      </aside>

      <div className="min-w-0 flex-1 px-[30px] pb-[56px] pt-[26px]">
        <Outlet />
      </div>
    </div>
  );
}

function NavItem({
  to,
  search,
  active,
  label,
}: {
  to: "/verkfaeri/markadurinn";
  search: { tab: Tab };
  active: boolean;
  label: string;
}) {
  return (
    <Link
      to={to}
      search={search}
      className={`rounded-[11px] px-3 py-2.5 text-left text-[13.5px] font-semibold transition ${
        active
          ? "bg-[#FEF1E3] text-[#F0871E]"
          : "text-muted hover:bg-canvas hover:text-ink"
      }`}
    >
      {label}
    </Link>
  );
}
